import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJobPostById, updateJobPost, JobPost } from '../../../../app/helper/jobService';
import { uploadImageToCloudinary } from '../../../../app/helper/functions';

const EditJobPost: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [jobPost, setJobPost] = useState<JobPost>({
        id: '',
        title: '',
        description: '',
        experience: '',
        vacancy: '',
        imageUrl: '',
        createdAt: null,
        createdBy: '',
        updatedAt: null,
        location: ''
    });

    useEffect(() => {
        if (id) {
            fetchJobPost();
        }
    }, [id]);

    const fetchJobPost = async () => {
        try {
            setLoading(true);
            const job = await getJobPostById(id!);
            if (job) {
                setJobPost(job);
                setPreviewUrl(job.imageUrl);
            } else {
                setError('Job post not found');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred fetching the job post');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setJobPost(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        // Check if file is an image
        if (!file.type.match('image.*')) {
            setError('Please select an image file');
            return;
        }

        // Check file size (limit to 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size should be less than 5MB');
            return;
        }

        setImageFile(file);
        setError(null);

        // Create a preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);

            let imageUrl = jobPost.imageUrl;

            // Only upload a new image if one was selected
            if (imageFile && previewUrl && !previewUrl.startsWith('http')) {
                const uploadResult = await uploadImageToCloudinary(previewUrl);
                if (!uploadResult) {
                    throw new Error('Failed to upload image');
                }
                imageUrl =
                    typeof uploadResult === 'string'
                        ? uploadResult
                        : uploadResult.url;
            }

            const updatedJobPost = {
                ...jobPost,
                imageUrl
            };

            const success = await updateJobPost(updatedJobPost);

            if (success) {
                navigate('/dashboard/');
            } else {
                setError('Failed to update job post');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred updating the job post');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {error}</span>
            </div>
        );
    }

    return (
        <div className="bg-blackDarkTheme max-w-2xl mx-auto p-6">
            <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 text-indigo-400 hover:text-indigo-300"
            >
                ← Back to Dashboard
            </button>
            <h2 className="text-2xl font-bold text-white mb-6">Edit Job Post</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">
                        Job Title
                    </label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={jobPost.title}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
                        Description
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={jobPost.description}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label htmlFor="experience" className="block text-sm font-medium text-gray-300 mb-1">
                        Experience Required
                    </label>
                    <input
                        type="text"
                        id="experience"
                        name="experience"
                        value={jobPost.experience}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label htmlFor="vacancy" className="block text-sm font-medium text-gray-300 mb-1">
                        Number of Vacancies
                    </label>
                    <input
                        type="number"
                        id="vacancy"
                        name="vacancy"
                        value={jobPost.vacancy}
                        onChange={handleChange}
                        required
                        min="1"
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-300 mb-1">
                        Location
                    </label>
                    <input
                        type="text"
                        id="location"
                        name="location"
                        value={jobPost.location}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                        Job Image
                    </label>
                    <div className="flex items-center space-x-4">
                        <div className="flex-1">
                            <input
                                id="image"
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="hidden"
                            />
                            <label
                                htmlFor="image"
                                className="flex items-center justify-center px-4 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                            >
                                Change Image
                            </label>
                        </div>
                        {previewUrl && (
                            <div className="w-24 h-24 relative">
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="w-full h-full object-cover rounded-md"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setImageFile(null);
                                        setPreviewUrl(jobPost.imageUrl);
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1 text-white text-xs"
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditJobPost; 